/**
 * ================================================================
 * API SERVICE: DashboardService
 * ================================================================
 *
 * @service          dashboardApi
 * @description      API service for dashboard data and client analytics
 * @baseUrl          VITE_API_BASE_URL environment variable
 *
 * @endpoints
 * - GET    /dashboard/fetch-summary-data - Dashboard summary statistics
 * - GET    /dashboard/fetch-client-dashboard - Client dashboard analytics
 * - GET    /clients/get-peak-margin-clients - Peak margin client data
 * - GET    /clients/get-short-margin - Short margin client analysis
 * - GET    /clients/get-client-kyc-data - KYC pending client information
 * - GET    /clients/get-tplus5-clients - T+5 client tracking
 * - GET    /clients/get-nptt-clients - NPTT client monitoring
 * - GET    /api/history/get-client-data - Client dormant data history
 * - GET    /dashboard/get-compliance-data-count - Compliance metrics
 * - GET    /dashboard/fetch-business-performance - Business performance analytics
 * - GET    /dashboard/fetch-recent-activity-data - Recent activity tracking
 *
 * @authentication  AuthToken header required for all endpoints
 * @rateLimit        Standard API rate limiting applies
 *
 * @caching
 * - Strategy: RTK Query automatic caching with feature-based tags
 * - Tags: Multiple tags for granular cache control
 * - TTL: 5 minutes for real-time dashboard data
 *
 * @errorHandling
 * - Global handler: transformApiError for consistent error management
 * - Auth errors: Automatic session clearing and login redirect
 * - Network errors: User-friendly error messages with retry options
 *
 * @types
 * - All request/response types imported from @/types/dashboard
 * - Centralized type management for better maintainability
 *
 * ================================================================
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiErrorTransformer, createBaseQuery } from '@/lib/api/baseQuery';
import type {
  BusinessPerformanceResponse,
  ClientDashboardApiResponse,
  ClientDormantDataRequest,
  ClientDormantDataResponse,
  ComplianceDataCountResponse,
  DashboardSummaryResponse,
  GlobalBrokerageSummaryData,
  RecentActivityResponse,
} from '@/types/dashboard';

const BALANCE_SHEET_API_URL = import.meta.env.VITE_APP_URL_COMPLIANCE;

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery: createBaseQuery(
    import.meta.env.VITE_API_BASE_URL || 'https://web.arhamshare.com/apb/v2',
  ),
  tagTypes: [
    'DashboardSummary',
    'getGlobalBrokerageSummaryData',
    'ClientDashboard',
    'PeakMarginClients',
    'ShortMarginClients',
    'KycPendingClients',
    'TPlus5Clients',
    'NPTTClients',
    'SortMarginClients',
    'ClientDormantData',
    'ComplianceDataCount',
    'BusinessPerformance',
    'RecentActivity',
  ],
  endpoints: (builder) => ({
    fetchDashboardSummary: builder.query<DashboardSummaryResponse, { financeYear: number }>({
      query: ({ financeYear }) => ({
        url: `/dashboard/fetch-summary-data`,
        method: 'GET',
        params: { financeYear },
      }),
      providesTags: (_result, _error, { financeYear }) => [
        { type: 'DashboardSummary', id: financeYear },
        'DashboardSummary',
      ],
      keepUnusedDataFor: 300, // Keep data for 5 minutes
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch dashboard summary');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('DashboardSummary', true),
    }),
    getGlobalBrokerageSummaryData: builder.query<
      GlobalBrokerageSummaryData,
      { financeYear: number }
    >({
      query: ({ financeYear }) => ({
        url: `/accounts/get-global-brokerage-summary-data`,
        method: 'GET',
        params: { financialYear: financeYear },
      }),
      providesTags: (_result, _error, { financeYear }) => [
        { type: 'getGlobalBrokerageSummaryData', id: financeYear },
        'getGlobalBrokerageSummaryData',
      ],
      keepUnusedDataFor: 300, // Keep data for 5 minutes
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch dashboard summary');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('getGlobalBrokerageSummaryData', true),
    }),
    // New endpoints for dashboard cards
    getPeakMarginClients: builder.query<
      any,
      {
        financeYear: number;
        snapShot: number;
        pageNumber: number;
        pageSize: number;
        searchTerm?: string;
      }
    >({
      query: ({ financeYear, snapShot, pageNumber, pageSize, searchTerm = '' }) => ({
        url: `/clients/get-peak-margin-clients`,
        method: 'GET',
        params: { financeYear, snapShot, pageNumber, pageSize, searchTerm },
      }),
      providesTags: (_result, _error, { financeYear }) => [
        { type: 'PeakMarginClients', id: financeYear },
        'PeakMarginClients',
      ],
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch peak margin clients');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('PeakMarginClients', false),
    }),
    getShortMarginClients: builder.query<
      any,
      {
        financeYear: number;
        snapShot: number;
        pageNumber: number;
        pageSize: number;
        searchTerm?: string;
      }
    >({
      query: ({ financeYear, snapShot, pageNumber, pageSize, searchTerm = '' }) => ({
        url: `/clients/get-short-margin`,
        method: 'GET',
        params: { financeYear, snapShot, pageNumber, pageSize, searchTerm },
      }),
      providesTags: (_result, _error, { financeYear }) => [
        { type: 'ShortMarginClients', id: financeYear },
        'ShortMarginClients',
      ],
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch short margin clients');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('ShortMarginClients', false),
    }),
    getKycPendingClients: builder.query<
      any,
      { financeYear: number; pageNumber: number; pageSize: number; searchTerm?: string }
    >({
      query: ({ financeYear, pageNumber, pageSize, searchTerm = '' }) => ({
        url: `/clients/get-client-kyc-data`,
        method: 'GET',
        params: { financeYear, pageNumber, pageSize, searchTerm, kycStatus: 'pending' },
      }),
      providesTags: (_result, _error, { financeYear }) => [
        { type: 'KycPendingClients', id: `${financeYear}` },
        'KycPendingClients',
      ],
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch KYC pending clients');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('KycPendingClients', false),
    }),
    getTPlus5Clients: builder.query<
      any,
      { financeYear: number; pageNumber: number; pageSize: number; searchTerm?: string }
    >({
      query: ({ financeYear, pageNumber, pageSize, searchTerm = '' }) => ({
        url: `/clients/get-tplus5-clients`,
        method: 'GET',
        params: { financeYear, pageNumber, pageSize, searchTerm },
      }),
      providesTags: (_result, _error, { financeYear }) => [
        { type: 'TPlus5Clients', id: financeYear },
        'TPlus5Clients',
      ],
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch T+5 clients');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('TPlus5Clients', false),
    }),
    getNPTTClients: builder.query<
      any,
      { financeYear: number; pageNumber: number; pageSize: number; searchTerm?: string }
    >({
      query: ({ financeYear, pageNumber, pageSize, searchTerm = '' }) => ({
        url: `/clients/get-nptt-clients`,
        method: 'GET',
        params: { financeYear, pageNumber, pageSize, searchTerm },
      }),
      providesTags: (_result, _error, { financeYear }) => [
        { type: 'NPTTClients', id: financeYear },
        'NPTTClients',
      ],
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch NPTT clients');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('NPTTClients', false),
    }),
    // Add mutation to invalidate cache when year changes
    invalidateYearCache: builder.mutation<void, { financeYear: number }>({
      queryFn: () => ({ data: undefined }), // No actual API call
      invalidatesTags: (_result, _error, { financeYear }) => [
        { type: 'DashboardSummary', id: financeYear },
        { type: 'PeakMarginClients', id: financeYear },
        { type: 'ShortMarginClients', id: financeYear },
        { type: 'TPlus5Clients', id: financeYear },
        { type: 'ComplianceDataCount', id: financeYear },
        { type: 'BusinessPerformance', id: financeYear },
        'DashboardSummary',
        'PeakMarginClients',
        'ShortMarginClients',
        'TPlus5Clients',
        'ComplianceDataCount',
        'BusinessPerformance',
      ],
    }),
    getHistoryClients: builder.query<ClientDormantDataResponse, ClientDormantDataRequest>({
      query: ({ tag, fromDate, toDate, searchTerm = '', pageNumber, pageSize, source }) => ({
        url: `${BALANCE_SHEET_API_URL}/history/get-client-data`,
        method: 'GET',
        params: { tag, fromDate, toDate, searchTerm, pageNumber, pageSize, source },
      }),
      providesTags: ['ClientDormantData'],
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch sort margin clients');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('HistoryClients', false),
    }),
    getComplianceDataCount: builder.query<ComplianceDataCountResponse, void>({
      query: () => ({
        url: `/dashboard/get-compliance-data-count`,
        method: 'GET',
      }),
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch compliance data count');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('ComplianceDataCount', true),
    }),
    fetchBusinessPerformance: builder.query<BusinessPerformanceResponse, { financeYear: number }>({
      query: ({ financeYear }) => ({
        url: `/dashboard/fetch-business-performance`,
        method: 'GET',
        params: { financeYear },
      }),
      providesTags: (_result, _error, { financeYear }) => [
        { type: 'BusinessPerformance', id: financeYear },
        'BusinessPerformance',
      ],
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch business performance data');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('BusinessPerformance', true),
    }),
    fetchRecentActivityData: builder.query<RecentActivityResponse, void>({
      query: () => ({
        url: `/dashboard/fetch-recent-activity-data`,
        method: 'GET',
        params: {
          source: 'ap',
        },
      }),
      providesTags: ['RecentActivity'],
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch recent activity data');
        }
        return response;
      },
      transformErrorResponse: createApiErrorTransformer('RecentActivity', true),
    }),
  }),
});

export const {
  useFetchDashboardSummaryQuery,
  useGetGlobalBrokerageSummaryDataQuery,
  useGetPeakMarginClientsQuery,
  useGetShortMarginClientsQuery,
  useGetKycPendingClientsQuery,
  useGetTPlus5ClientsQuery,
  useGetNPTTClientsQuery,
  useGetHistoryClientsQuery,
  useInvalidateYearCacheMutation,
  useGetComplianceDataCountQuery,
  useFetchBusinessPerformanceQuery,
  useFetchRecentActivityDataQuery,
} = dashboardApi;
